import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Tooltip,
  LinearProgress,
  Collapse,
  IconButton,
} from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { getActivityStockSummary } from "../../api/animalAttendance";

export default function StockTakingActivitySummary({
  activityId,
  tenantId,
  dataVersion = 0,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartOpen, setChartOpen] = useState(true);

  useEffect(() => {
    if (activityId == null) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getActivityStockSummary(activityId, {
          tenantId: tenantId ?? null,
        });
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) {
          const msg =
            typeof e === "string"
              ? e
              : e?.detail || e?.message || "Could not load activity summary";
          setError(msg);
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activityId, tenantId, dataVersion]);

  if (activityId == null) return null;

  const act = data?.activity;
  const archived = Boolean(act?.deleted_at);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && (
        <Typography color="error" variant="body2">
          {String(error)}
        </Typography>
      )}
      {data && act && (
        <>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {act.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {act.activity_type} · {act.status} · {act.start_date} →{" "}
                {act.end_date}
              </Typography>
            </Box>
            {archived && (
              <Chip
                size="small"
                label="Archived"
                variant="outlined"
                component="span"
                clickable={false}
                sx={{ pointerEvents: "none", cursor: "default" }}
              />
            )}
          </Box>

          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            sx={{ mt: 2, mb: 1 }}
          >
            <Chip
              color="success"
              variant="outlined"
              label={`Present: ${data.counts.present}`}
              component="span"
              clickable={false}
              sx={{ pointerEvents: "none", cursor: "default" }}
            />
            <Chip
              color="warning"
              variant="outlined"
              label={`Absent: ${data.counts.absent}`}
              component="span"
              clickable={false}
              sx={{ pointerEvents: "none", cursor: "default" }}
            />
            <Chip
              variant="outlined"
              label={`Unchecked: ${data.counts.unchecked}`}
              component="span"
              clickable={false}
              sx={{ pointerEvents: "none", cursor: "default" }}
            />
            <Chip
              variant="filled"
              color="primary"
              label={`Recorded animals: ${data.animals_with_any_record} / ${data.active_animals_in_herd}`}
              component="span"
              clickable={false}
              sx={{ pointerEvents: "none", cursor: "default" }}
            />
            <Chip
              variant="filled"
              color="secondary"
              label={`Herd touched: ${data.pct_animals_touched}%`}
              component="span"
              clickable={false}
              sx={{ pointerEvents: "none", cursor: "default" }}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {data.animals_not_recorded > 0
              ? `${data.animals_not_recorded} active animals have no stock-taking row yet for this activity (any day).`
              : "Every active animal has at least one row for this activity."}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              By day (this activity)
            </Typography>
            <IconButton
              size="small"
              aria-label={chartOpen ? "Hide chart" : "Show chart"}
              onClick={() => setChartOpen((v) => !v)}
            >
              {chartOpen ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={chartOpen}>
            <Box
              sx={{
                display: "flex",
                gap: 0.35,
                flexWrap: "wrap",
                alignItems: "flex-end",
                mt: 1,
                maxWidth: "100%",
                overflowX: "auto",
              }}
            >
              {data.by_date.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No rows yet — mark attendance to see daily breakdown.
                </Typography>
              ) : (
                data.by_date.map((row) => {
                  const total =
                    row.present + row.absent + row.unchecked || 1;
                  const ph = Math.round((row.present / total) * 100);
                  const ah = Math.round((row.absent / total) * 100);
                  const uh = 100 - ph - ah;
                  return (
                    <Tooltip
                      key={row.date}
                      title={`${row.date}: Present ${row.present}, Absent ${row.absent}, Unchecked ${row.unchecked}`}
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 36,
                          display: "flex",
                          flexDirection: "column-reverse",
                          borderRadius: 0.5,
                          overflow: "hidden",
                          flexShrink: 0,
                        }}
                      >
                        <Box sx={{ height: `${uh}%`, bgcolor: "grey.400" }} />
                        <Box sx={{ height: `${ah}%`, bgcolor: "warning.main" }} />
                        <Box sx={{ height: `${ph}%`, bgcolor: "success.main" }} />
                      </Box>
                    </Tooltip>
                  );
                })
              )}
            </Box>
          </Collapse>
        </>
      )}
    </Paper>
  );
}
